import { createSelector, createSlice } from '@reduxjs/toolkit';

import { AppFullLocale, SlicePayload } from '@/types';
import { StoreConfModel } from '@/models';
import type { RootState as AppRootState } from '@/redux/create-store';
import { STORE_ID_DEFAULT } from '@/constants/stores';

export type RootState = {
  storeConf: StoreConfModel;
  currencyLocale: string;
  ratingValue: number;
  ratingCount: number;
  locales: AppFullLocale[];
  baseUrl: string;
  isTestEnv: boolean;
}

export const initialState: RootState = {
  storeConf: {
    storeId: STORE_ID_DEFAULT,
    locale: 'fr-FR',
    site: 'FR',
  } as StoreConfModel,
  currencyLocale: 'fr_FR',
  ratingValue: 0,
  ratingCount: 0,
  locales: [],
  baseUrl: '',
  isTestEnv: false
};

const root = createSlice({
  name: 'root',
  initialState,
  reducers: {
    setStoreConf: (state, action: SlicePayload<{ storeConf: StoreConfModel }>) => {
      state.currencyLocale = action.payload.storeConf.locale.replace('-', '_');
      state.storeConf = action.payload.storeConf;
    },

    setRatings: (state, action: SlicePayload<{ ratingValue: number, ratingCount: number }>) => {
      const { ratingValue, ratingCount } = action.payload;
      state.ratingValue = ratingValue;
      state.ratingCount = ratingCount;
    },

    setLocales: (state, action: SlicePayload<{ locales: AppFullLocale[] }>) => {
      state.locales = action.payload.locales;
    },

    setBaseUrl: (state, action: SlicePayload<{ baseUrl: string }>) => {
      state.baseUrl = action.payload.baseUrl;
    }
  }
});

export const {
  setStoreConf, setRatings, setLocales, setBaseUrl
} = root.actions;

export default root.reducer;

const storeConfContent = (state: AppRootState) => state.root.storeConf;

export const selectStoreConf = createSelector(
  [storeConfContent],
  (storeConf) => storeConf
);

export const selectLocale = createSelector(
  [storeConfContent],
  (storeConf) => storeConf.locale
);

export const selectStoreId = createSelector(
  [storeConfContent],
  (storeConf) => storeConf.storeId
);

export const selectCurrencyIso = createSelector(
  [storeConfContent],
  (storeConf) => storeConf.currencyISO
);

export const selectCodeISO = createSelector(
  [storeConfContent],
  (storeConf) => storeConf.codeISO
);
